// ISDP CUSTOM: forwards Backstage analytics events to the DevHub backend for
// anonymous, first-party usage stats (page views + actions). Identity is never
// sent — the backend derives + hashes it. Errors are swallowed so tracking never
// affects the user experience (e.g. unauthenticated public pages get a 401 → ignored).
import {
  AnalyticsApi,
  AnalyticsEvent,
  ConfigApi,
  FetchApi,
} from '@backstage/core-plugin-api';

export class UsageAnalyticsApi implements AnalyticsApi {
  private readonly baseUrl: string;

  constructor(
    private readonly fetchApi: FetchApi,
    configApi: ConfigApi,
  ) {
    this.baseUrl = configApi.getString('backend.baseUrl');
  }

  captureEvent(event: AnalyticsEvent): void {
    try {
      const isNav = event.action === 'navigate';
      // 'search' action → a first-class search event carrying the raw term (server scrubs PII) + the
      // result count (0 == couldn't find). Everything else keeps the existing page_view / action mapping.
      const isSearch = event.action === 'search';
      let type: 'page_view' | 'search' | 'action' = 'action';
      if (isNav) type = 'page_view';
      else if (isSearch) type = 'search';
      const fallbackPath =
        typeof window !== 'undefined' ? window.location.pathname : event.subject;
      const path = isNav ? event.subject : fallbackPath;
      let label: string | undefined;
      if (isNav) label = undefined;
      else if (isSearch) label = String(event.subject).slice(0, 128);
      else label = `${event.action}:${event.subject}`.slice(0, 128);
      const count = isSearch ? Number((event.attributes as any)?.count) : undefined;
      // fire-and-forget; never block or throw into the caller
      void this.fetchApi
        .fetch(`${this.baseUrl}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ type, path, label, count }),
        })
        .catch(() => {});
    } catch {
      /* analytics must never break the app */
    }
  }
}

// Forwards each event to several AnalyticsApi implementations (e.g. GA4 + first-party).
export class CompositeAnalyticsApi implements AnalyticsApi {
  constructor(private readonly delegates: AnalyticsApi[]) {}

  captureEvent(event: AnalyticsEvent): void {
    for (const d of this.delegates) {
      try {
        d.captureEvent(event);
      } catch {
        /* one sink failing must not stop the others */
      }
    }
  }
}
